// createTrigger — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-trigger' })` op (DSE2 §3.D6).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.
//
// Per-engine surface (per DSE2 dialect.trigger):
//   - mssql:  CREATE [OR ALTER] TRIGGER <name> ON <target>
//             {AFTER|INSTEAD OF} <events> AS <bodyText>
//             — no BEFORE; statement-level only
//   - pgsql:  CREATE [OR REPLACE] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <events> ON <target>
//             [FOR EACH {ROW|STATEMENT}] [WHEN (cond)]
//             EXECUTE FUNCTION <pgsqlExecuteFunction>
//             — body lives in a separately-defined function (pgsqlExecuteFunction)
//   - mysql:  CREATE TRIGGER <name> {BEFORE|AFTER} <event> ON <target>
//             FOR EACH ROW <bodyText>
//             — single event only; no INSTEAD OF
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <events> ON <target>
//             [FOR EACH ROW] <bodyText>
//   - sqlite: CREATE [TEMP] TRIGGER <name>
//             {BEFORE|AFTER|INSTEAD OF} <event> ON <target>
//             [FOR EACH ROW] [WHEN cond] <bodyText>

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, TriggerShellDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateTriggerArgs {
  readonly trigger: TriggerShellDef;
}

export function buildCreateTriggerEdit(args: CreateTriggerArgs): DdlEdit {
  return { op: 'create-trigger', trigger: args.trigger };
}

export async function commitCreateTrigger(
  ctx: VqeDdlEditContext,
  args: CreateTriggerArgs,
): Promise<void> {
  const edit = buildCreateTriggerEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const CREATE_TRIGGER_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle', 'sqlite',
] as const);

/** All five engines support CREATE TRIGGER. The per-engine surface diverges
 *  on supported timings, multi-event shapes, and required fields, but every
 *  engine has a CREATE TRIGGER form. */
export function isCreateTriggerSupported(engine: SqlEngine): boolean {
  return CREATE_TRIGGER_SUPPORTED.has(engine);
}

/** Engines accepting a `BEFORE` timing on CREATE TRIGGER. mssql is the
 *  exception — T-SQL has no BEFORE form. */
export function isBeforeTimingSupported(engine: SqlEngine): boolean {
  return engine !== 'mssql';
}

/** Engines accepting an `INSTEAD OF` timing on CREATE TRIGGER. mysql is the
 *  exception — its trigger model has no INSTEAD OF form (use BEFORE/AFTER). */
export function isInsteadOfTimingSupported(engine: SqlEngine): boolean {
  return engine !== 'mysql';
}

/** Engines accepting multi-event triggers (`AFTER INSERT, UPDATE`). mysql /
 *  sqlite require one event per trigger and reject multi-event shapes. */
export function isMultiEventSupported(engine: SqlEngine): boolean {
  return engine !== 'mysql' && engine !== 'sqlite';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateTriggerForEngine(
  engine: SqlEngine,
  args: CreateTriggerArgs,
): string | null {
  const t = args.trigger;
  if (t.name.name.trim().length === 0) {
    return 'Trigger name is required.';
  }
  if (t.target.name.trim().length === 0) {
    return 'Target table is required.';
  }
  if (t.events.length === 0) {
    return 'At least one event (INSERT / UPDATE / DELETE) is required.';
  }
  if (!isMultiEventSupported(engine) && t.events.length > 1) {
    return `${engine} triggers fire on a single event only — split into per-event triggers.`;
  }
  if (!isBeforeTimingSupported(engine) && t.timing === 'before') {
    return 'mssql has no BEFORE trigger form. Use AFTER + post-validation, or INSTEAD OF.';
  }
  if (!isInsteadOfTimingSupported(engine) && t.timing === 'instead-of') {
    return 'mysql has no INSTEAD OF trigger form. Use BEFORE / AFTER on the base table.';
  }
  if (engine === 'postgres') {
    if (!t.pgsqlExecuteFunction || t.pgsqlExecuteFunction.trim().length === 0) {
      return 'PostgreSQL triggers reference a separately-defined function via EXECUTE FUNCTION fn().';
    }
  } else if (t.pgsqlExecuteFunction) {
    return `${engine} does not accept pgsqlExecuteFunction (PostgreSQL-only).`;
  }
  if (engine !== 'postgres' && engine !== 'oracle' && t.orReplace) {
    return `${engine} does not support OR REPLACE on CREATE TRIGGER.`;
  }
  if (engine !== 'mssql' && t.mssqlOrAlter) {
    return `${engine} does not accept mssqlOrAlter (mssql-only).`;
  }
  if (engine !== 'oracle' && (t.oracleEditionable || t.oracleNonEditionable)) {
    return `${engine} does not accept EDITIONABLE / NONEDITIONABLE (Oracle-only).`;
  }
  if (t.oracleEditionable && t.oracleNonEditionable) {
    return 'EDITIONABLE and NONEDITIONABLE are mutually exclusive.';
  }
  if (engine !== 'sqlite' && t.sqliteTemporary) {
    return `${engine} does not accept TEMP TRIGGER (SQLite-only).`;
  }
  if (engine === 'postgres') {
    // pgsql ignores bodyText.
  } else if (t.bodyText.trim().length === 0) {
    return 'Trigger body is required.';
  }
  return null;
}
